import { useEffect, useRef, useState } from "react";
import {
  FIGURINHA_MAX_BYTES,
  FIGURINHA_NOME_MAX,
  TIPOS_DE_FIGURINHA,
  type FigurinhaDaBiblioteca,
  type TipoDeFigurinha,
} from "@foto/shared";
import type { ProgressoUpload } from "../../lib/api.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { Botao, Campo, Entrada, Girando, cx } from "../../components/ui.js";
import { Aviso } from "./Aviso.js";
import { CabecalhoDoPainel } from "./CabecalhoDoPainel.js";
import { nomeSugerido, pesoLegivel } from "./apresentacao.js";
import { ACEITA_NO_SELETOR, conferirArquivo } from "./arquivo.js";
import { enviarFigurinha } from "./enviar.js";
import { mensagemDeErro } from "./erros.js";
import { EXPLICACAO_DO_TIPO, ROTULO_DO_TIPO } from "./tipos.js";

const LIMITE_EM_MB = FIGURINHA_MAX_BYTES / (1024 * 1024);

/**
 * "Enviar imagem": o tipo, o arquivo e (se quiser) o nome. Ao terminar, entrega a imagem criada, com a
 * descrição que o servidor já escreveu, para quem abriu mostrar o detalhe dela.
 *
 * Uma imagem por vez: o tipo e o nome são de cada uma, e um lote traria a pergunta "esse tipo vale para
 * todas?" a quem está de pé, com o celular na mão.
 *
 * O tipo é escolhido **antes**, sem padrão: é a única decisão que depois só se desfaz reenviando a imagem
 * (o servidor não troca o tipo), e um padrão faria muita figurinha nascer como "Logo".
 */
export function EnviarFigurinha({
  aoFechar,
  aoEnviada,
}: {
  aoFechar: () => void;
  aoEnviada: (item: FigurinhaDaBiblioteca) => void;
}) {
  const [tipo, setTipo] = useState<TipoDeFigurinha | null>(null);
  const [nome, setNome] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  /** A recusa do próprio celular (tipo, peso): nem chegou a ir para a rede. */
  const [recusaDoArquivo, setRecusaDoArquivo] = useState<string | null>(null);
  /** A recusa do servidor, com as palavras dele. */
  const [erro, setErro] = useState<string | null>(null);
  const [progresso, setProgresso] = useState<ProgressoUpload | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [previa, setPrevia] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  // A prévia é o próprio arquivo: o operador vê o recorte (no xadrez) antes de gastar rede, e uma imagem
  // que não está recortada se denuncia sozinha.
  useEffect(() => {
    if (!arquivo) {
      setPrevia(null);
      return;
    }
    const url = URL.createObjectURL(arquivo);
    setPrevia(url);
    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  function escolher(file: File | undefined) {
    if (!file) return;
    setErro(null);
    const recusa = conferirArquivo(file);
    setRecusaDoArquivo(recusa);
    setArquivo(recusa ? null : file);
  }

  async function enviar() {
    if (!tipo || !arquivo || enviando) return;
    setErro(null);
    setEnviando(true);
    setProgresso({ enviados: 0, total: arquivo.size, percent: 0 });
    try {
      aoEnviada(await enviarFigurinha(arquivo, tipo, nome, setProgresso));
    } catch (e) {
      setErro(mensagemDeErro(e, "Não foi possível enviar a imagem."));
      setEnviando(false);
      setProgresso(null);
    }
  }

  const pronto = tipo !== null && arquivo !== null;
  const percent = progresso?.percent ?? 0;
  // O arquivo já chegou inteiro: o que resta é o servidor descrever a imagem (até ~30 s).
  const descrevendo = enviando && percent >= 100;

  return (
    <Sobreposicao rotulo="Enviar imagem" aoFechar={() => !enviando && aoFechar()}>
      <CabecalhoDoPainel titulo="Enviar imagem" aoFechar={aoFechar} fecharDesligado={enviando} />

      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void enviar();
        }}
      >
        {enviando && arquivo && tipo ? (
          // Enquanto envia, o formulário sai de cena: com ele na tela o andamento ficava abaixo da dobra,
          // atrás do rodapé fixo, e quem tocou em "Enviar" não via nada acontecer por até meio minuto.
          <div className="flex flex-col gap-4 px-4 py-5 sm:px-6">
            <div className="flex items-center gap-3 rounded-peca border border-borda bg-superficie p-3">
              <span className="fundo-xadrez block size-16 shrink-0 overflow-hidden rounded-peca border border-borda">
                {previa && <img src={previa} alt="" className="size-full object-contain p-1" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.9375rem] text-texto">
                  {nome.trim() || nomeSugerido(arquivo.name, FIGURINHA_NOME_MAX)}
                </p>
                <p className="text-[0.8125rem] text-texto-fraco">
                  {ROTULO_DO_TIPO[tipo]} · <span className="num">{pesoLegivel(arquivo.size)}</span>
                </p>
              </div>
            </div>

            <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-[0.9375rem]">
                  <Girando className="text-projecao" />
                  Enviando e descrevendo a imagem…
                </span>
                <span aria-hidden className="num font-mono text-[0.8125rem] text-texto-suave">
                  {percent}%
                </span>
              </div>
              <div
                role="progressbar"
                aria-label="Envio da imagem"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                className="h-1 overflow-hidden rounded-full bg-borda"
              >
                <div
                  className={cx(
                    "h-full rounded-full transition-[width]",
                    descrevendo ? "revelando bg-projecao" : "bg-brasa",
                  )}
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p aria-live="polite" className="mt-2 text-[0.8125rem] leading-snug text-texto-fraco">
                {descrevendo
                  ? "Imagem recebida. A IA está olhando a imagem para descrevê-la; pode levar até meio minuto."
                  : "Enviando o arquivo…"}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5 px-4 py-5 sm:px-6">
            {/* No alto, e não no pé: ao voltar da recusa o painel recomeça rolado para cima, e a frase
                precisa ser a primeira coisa que se lê — ela diz o que fazer com a imagem. */}
            {erro && (
              <Aviso tom="perigo">
                <p role="alert" className="wrap-anywhere">
                  {erro}
                </p>
              </Aviso>
            )}

            <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
              PNG ou WebP com <strong className="font-medium text-texto">fundo transparente</strong>
              , até {LIMITE_EM_MB} MB. Uma imagem de cada vez.
            </p>

            <fieldset className="min-w-0">
              <legend className="mb-1.5 text-[0.8125rem] font-medium text-texto-suave">
                1. Que tipo de imagem é?
              </legend>
              <div className="grid gap-2 sm:grid-cols-3">
                {TIPOS_DE_FIGURINHA.map((t) => (
                  <label
                    key={t}
                    className={cx(
                      "group flex min-h-11 cursor-pointer flex-col gap-1 rounded-peca border border-borda bg-superficie px-3 py-2.5 transition-colors",
                      "hover:border-borda-forte has-checked:border-brasa has-checked:bg-brasa-fraca/40",
                      "has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-brasa",
                    )}
                  >
                    <input
                      type="radio"
                      name="tipo"
                      value={t}
                      checked={tipo === t}
                      onChange={() => setTipo(t)}
                      className="sr-only"
                    />
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-[0.9375rem] font-medium text-texto">
                        {ROTULO_DO_TIPO[t]}
                      </span>
                      {/* O que marca a escolha não pode ser só a cor da borda. */}
                      <span
                        aria-hidden
                        className="size-4 shrink-0 rounded-full border border-borda-forte group-has-checked:border-brasa group-has-checked:bg-brasa"
                      />
                    </span>
                    <span className="text-[0.8125rem] leading-snug text-texto-suave">
                      {EXPLICACAO_DO_TIPO[t]}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div>
              <p className="mb-1.5 text-[0.8125rem] font-medium text-texto-suave">2. A imagem</p>
              <input
                ref={seletor}
                type="file"
                accept={ACEITA_NO_SELETOR}
                hidden
                onChange={(e) => {
                  escolher(e.target.files?.[0]);
                  // Sem isto, escolher de novo o mesmo arquivo (depois de corrigi-lo) não dispara nada.
                  e.target.value = "";
                }}
              />

              {arquivo ? (
                <div className="flex items-center gap-3 rounded-peca border border-borda bg-superficie p-3">
                  <span className="fundo-xadrez block size-24 shrink-0 overflow-hidden rounded-peca border border-borda">
                    {previa && (
                      <img
                        src={previa}
                        alt={`Prévia de ${arquivo.name}`}
                        className="size-full object-contain p-1"
                      />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[0.9375rem] text-texto">{arquivo.name}</p>
                    <p className="num text-[0.8125rem] text-texto-fraco">
                      {pesoLegivel(arquivo.size)}
                    </p>
                    <Botao
                      type="button"
                      tom="fantasma"
                      formato="nu"
                      onClick={() => seletor.current?.click()}
                      className="mt-1 min-h-11"
                    >
                      Trocar a imagem
                    </Botao>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => seletor.current?.click()}
                  className="flex min-h-28 w-full flex-col items-center justify-center gap-1 rounded-peca border border-dashed border-borda-forte bg-superficie px-4 py-5 text-center transition-colors hover:border-brasa hover:bg-superficie-alta"
                >
                  <span className="text-[0.9375rem] font-medium text-texto">Escolher a imagem</span>
                  <span className="text-[0.8125rem] text-texto-fraco">
                    PNG ou WebP, fundo transparente, até {LIMITE_EM_MB} MB
                  </span>
                </button>
              )}

              {recusaDoArquivo && (
                <p role="alert" className="mt-1.5 text-[0.8125rem] text-descartar">
                  {recusaDoArquivo}
                </p>
              )}
            </div>

            <Campo
              rotulo="3. Nome (opcional)"
              dica={
                arquivo
                  ? "Sem nome, ficam as letras do arquivo."
                  : "Para achar a imagem depois, na busca."
              }
            >
              <Entrada
                value={nome}
                maxLength={FIGURINHA_NOME_MAX}
                placeholder={
                  arquivo ? nomeSugerido(arquivo.name, FIGURINHA_NOME_MAX) : "Ex.: Coração vermelho"
                }
                onChange={(e) => setNome(e.target.value)}
              />
            </Campo>
          </div>
        )}

        <footer className="area-segura-inferior sticky bottom-0 border-t border-borda bg-fundo px-4 pt-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <Botao type="submit" tom="brasa" disabled={!pronto} carregando={enviando}>
              {enviando ? "Enviando…" : "Enviar imagem"}
            </Botao>
            <Botao type="button" tom="fantasma" disabled={enviando} onClick={aoFechar}>
              Cancelar
            </Botao>
            {!pronto && !enviando && (
              <p className="basis-full text-[0.8125rem] text-texto-fraco sm:basis-auto">
                {tipo === null
                  ? "Escolha o tipo da imagem para poder enviar."
                  : "Escolha a imagem para poder enviar."}
              </p>
            )}
          </div>
        </footer>
      </form>
    </Sobreposicao>
  );
}
